"use client";

import { useTranslations } from "next-intl";

const FEATURE_KEYS = [
	{ numberKey: "feature1Number", titleKey: "feature1Title", descKey: "feature1Desc" },
	{ numberKey: "feature2Number", titleKey: "feature2Title", descKey: "feature2Desc" },
	{ numberKey: "feature3Number", titleKey: "feature3Title", descKey: "feature3Desc" },
	{ numberKey: "feature4Number", titleKey: "feature4Title", descKey: "feature4Desc" },
] as const;

/** 星标图标（用于徽章顶部） */
function StarIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
		</svg>
	);
}

export interface RapidToolingWhyChooseUsProps {
	translationNamespace: string;
}

/**
 * Rapid Tooling Why Choose Us：顶部左徽章右文案，底部 4 个白卡片网格（绿底数字 + 标题 + 正文）
 */
export default function RapidToolingWhyChooseUs({ translationNamespace }: RapidToolingWhyChooseUsProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 顶部：左侧绿色徽章 + 右侧标题+描述 */}
				<div className="mb-6 flex flex-col gap-8 sm:mb-8 md:mb-10 lg:mb-12 lg:flex-row lg:items-start lg:gap-10 lg:px-[10%] xl:gap-12">
					{/* 左侧：绿色 ISO 认证徽章 */}
					<div className="flex shrink-0 flex-col items-center justify-center rounded-lg bg-themePrimary400 px-6 py-6 text-center text-white shadow-lg sm:px-6 sm:py-6 md:px-6 md:py-6 lg:w-[240px] xl:w-[280px]">
						<StarIcon className="mb-3 h-6 w-6 text-white sm:h-7 sm:w-7" />
						<p className="mb-2 text-xs font-medium sm:text-sm">{t("badgeTopText")}</p>
						<p className="mb-2 text-2xl font-bold sm:text-3xl md:text-4xl">{t("badgeMainText")}</p>
						<p className="text-xs font-medium sm:text-sm">{t("badgeBottomText")}</p>
					</div>

					{/* 右侧：标题 + 描述 */}
					<div className="min-w-0 flex-1">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					</div>
				</div>

				{/* 底部：4 个白色卡片网格 */}
				<div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
					{FEATURE_KEYS.map((key) => (
						<div key={key.titleKey} className="flex flex-col bg-white p-6 pt-0 shadow-card">
							{/* 绿色数字方块 */}
							<div
								className="mb-4 flex h-10 w-10 shrink-0 items-center justify-center bg-themePrimary400 text-base font-bold text-white sm:mb-5"
								aria-hidden
							>
								{t(key.numberKey)}
							</div>
							<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">{t(key.titleKey)}</h3>
							<p className="text-sm leading-relaxed text-black md:text-base">{t(key.descKey)}</p>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
